import { FC, useCallback, useEffect, useState } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { captureException } from '@sentry/react';
import { Input } from 'antd';
import { TbLayoutGridAdd } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { userService, workspaceService } from '@/services/database';
import { PlatformForm as Form } from '@/shared/components/forms';
import WorkspaceTemplateSelector from '@/shared/components/workspace-resources/WorkspaceTemplateSelector';
import { useUserStore } from '@/stores';
import { WorkspaceTemplateKey } from '@/types/workspaceTemplates';

interface CreateWorkspaceProps {
    onFooterStateChange?: (state: { disabled: boolean; loading: boolean; onCreate: () => void }) => void;
}

const CreateWorkspace: FC<CreateWorkspaceProps> = ({ onFooterStateChange }): JSX.Element => {
    const [name, setName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [templateKey, setTemplateKey] = useState<WorkspaceTemplateKey>('newWorkspace');

    const { selectedUser: user, fetchUser } = useUserStore();

    const navigate = useNavigate();

    const { token } = useAntDesignTheme();

    const handleCreateNewWorkspace = useCallback(async () => {
        const normalizedName = name.trim();

        if (!normalizedName || submitting) {
            return;
        }

        setSubmitting(true);

        try {
            const workspace = await workspaceService.create({
                name: normalizedName,
                templateKey,
            });

            toast('Creating Workspace...', {
                icon: <LoadingOutlined spin />,
                description: workspace.name,
            });

            if (!user?.id) {
                throw new Error('Authenticated user is unavailable after workspace creation.');
            }

            await userService.updateActiveWorkspace(user.id, workspace.id);
            await fetchUser();
            navigate('/', { replace: true });
        } catch (error) {
            captureException(error, { extra: { templateKey } });
            toast.error('Unable to Create Workspace', {
                description: 'Please try again or contact support if the issue persists.',
            });
        } finally {
            setSubmitting(false);
        }
    }, [fetchUser, name, navigate, submitting, templateKey, user?.id]);

    useEffect(() => {
        onFooterStateChange?.({
            disabled: name.trim() === '' || submitting,
            loading: submitting,
            onCreate: handleCreateNewWorkspace,
        });
    }, [handleCreateNewWorkspace, name, onFooterStateChange, submitting]);

    return (
        <div>
            <Form layout='vertical' onFinish={handleCreateNewWorkspace}>
                <Form.Item
                    label='Workspace Name'
                    style={{ marginBottom: '0.5rem' }}
                    rules={[
                        {
                            max: 100,
                            min: 1,
                            required: true,
                            message: 'Please input a name for your workspace.',
                        },
                    ]}
                >
                    <Input
                        addonBefore={
                            <div style={{ margin: '0 -0.125rem' }}>
                                <TbLayoutGridAdd
                                    size='1.25rem'
                                    style={{ color: token.colorTextTertiary }}
                                />
                            </div>
                        }
                        disabled={submitting}
                        maxLength={100}
                        onChange={(event) => setName(event.target.value)}
                        onPressEnter={handleCreateNewWorkspace}
                        placeholder='Graham-Newman Partnership...'
                        size='large'
                        value={name}
                    />
                </Form.Item>
                <Form.Item label='Workspace Template' style={{ marginBottom: 0 }}>
                    <WorkspaceTemplateSelector
                        bordered
                        disabled={submitting}
                        value={templateKey}
                        onChange={setTemplateKey}
                    />
                </Form.Item>
            </Form>
        </div>
    );
};

export default CreateWorkspace;
